import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Folder,
  Monitor,
  Settings,
  User,
  LogOut,
  LogIn,
  Shield,
  XCircle,
  CheckCircle2,
  Slash,
  Globe,
  FileText,
  Activity,
} from "lucide-react";

import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { useEffect, useMemo, useState } from "react";
import { TopRecentActivityProps } from "./interfaces/interface";
import { Skeleton } from "../interfaces/CommonInterfaces";
import OverviewCardHeader from "./OverviewCardHeader";
import OverviewEmptyState from "./OverviewEmptyState";

export default function OverviewRecentActivity({
  fromdetails = false,
  recentdata = [],
  loading,
}: TopRecentActivityProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(recentdata.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [recentdata]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedActivities = useMemo(
    () => recentdata.slice(startIndex, startIndex + itemsPerPage),
    [recentdata, startIndex]
  );

  // ---------- ICON HELPERS ----------
  const getIcons = (val: any) => {
    const rawEvent = val?.event || "";
    const str = rawEvent.trim().toLowerCase();

    const iconProps = {
      size: 24,
      color: getIconsColor(str),
      ...(fromdetails && { style: { color: `var(--overview-event-${getIconsColor(str)})` } }),
    };

    if (str.includes("resource control")) {
      const desc = val?.description?.toLowerCase() || "";
      if (desc.includes("app")) return <Settings {...iconProps} />;
      if (desc.includes("folder")) return <Folder {...iconProps} />;
      if (desc.includes("url")) return <Globe {...iconProps} />;
      return <Folder {...iconProps} />;
    }

    if (str.includes("audit")) {
      return getAuditIcon(val, iconProps);
    }

    switch (str) {
      case "install":
        return <Monitor {...iconProps} />;
      case "enforce":
      case "policy enforce":
        return <Shield {...iconProps} />;
      case "uninstall":
        return <XCircle {...iconProps} />;
      case "alert":
        return <AlertTriangle {...iconProps} />;
      case "appinstall blocked":
        return <Slash {...iconProps} />;
      case "appinstall allowed":
        return <CheckCircle2 {...iconProps} />;
      default:
        return <FileText {...iconProps} />;
    }
  };

  const getAuditIcon = (val: any, iconProps: any) => {
    const message = val?.description?.toLowerCase();
    if (message?.includes("logged off")) return <LogOut {...iconProps} />;
    if (message?.includes("logged in") || message?.includes("reconnected"))
      return <LogIn {...iconProps} />;
    return <FileText {...iconProps} />;
  };

  const getIconsColor = (val: string) => {
    if (val.includes("audit")) return "blue";

    switch (val) {
      case "install":
      case "appinstall allowed":
      case "login":
        return "green";
      case "enforce":
      case "uninstall":
      case "appinstall blocked":
      case "logout":
        return "red";
      case "alert":
        return "orange";
      default:
        return "blue";
    }
  };

  // ---------- SIMPLE PAGINATION ----------
  const getPagination = (currentPage: number, totalPages: number) => {
    const pages: (number | string)[] = [];
    const MAX_VISIBLE = 5;

    if (totalPages <= MAX_VISIBLE + 2) {
      // If total pages are small, show all
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const showLeftEllipsis = currentPage > 3;
      const showRightEllipsis = currentPage < totalPages - 2;

      if (!showLeftEllipsis && showRightEllipsis) {
        // Show beginning pages
        for (let i = 1; i <= MAX_VISIBLE; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (showLeftEllipsis && !showRightEllipsis) {
        // Show ending pages
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - MAX_VISIBLE + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle range
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };


  return (
    <Card className="overview-card overview-recent-card h-full flex flex-col">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Recent Activity"
        description="Latest recorded events and their endpoint context"
        icon={Activity}
        detail={!loading && recentdata.length > 0 ? `${recentdata.length.toLocaleString()} events` : undefined}
      />
      <CardContent className="overview-card-content overview-recent-content flex flex-col flex-1 p-4">
        <div className="overview-activity-list space-y-2 flex-1 overflow-auto">
          {loading ? (
            Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="overview-activity-entry p-3 border rounded-lg">
                <div className="flex items-start gap-3">
                  <Skeleton className="w-6 h-6 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="w-2/3 h-4" />
                    <Skeleton className="w-1/2 h-3" />
                    <Skeleton className="w-1/3 h-3" />
                  </div>
                  <Skeleton className="w-12 h-3" />
                </div>
              </div>
            ))
          ) : paginatedActivities.length > 0 ? (
            paginatedActivities.map((activity, index) => (
              <div
                key={index}
                className="overview-activity-entry p-3 border rounded-lg hover:bg-muted/40 transition-colors"
                data-testid={`activity-${activity.id}`}
              >
                <div className="overview-activity-row flex items-start gap-3">
                  <div className="overview-event-icon flex-shrink-0" aria-hidden="true">{getIcons(activity)}</div>
                  <div className="flex-1 min-w-0">
                    {fromdetails && (
                      <div className="overview-activity-topline">
                        <span className="overview-event-label">{activity.event}</span>
                        <span className="overview-event-time"><Clock aria-hidden="true" />{activity.time}</span>
                      </div>
                    )}
                    <div className="overview-activity-body flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="overview-event-description mb-2 text-sm font-medium break-all text-foreground">{activity.description}</div>
                        <div className="overview-event-context space-y-0.5">
                          <div className="overview-context-item flex items-center gap-2 text-xs text-muted-foreground">
                            <Monitor className="w-3 h-3" aria-hidden="true" />
                            <span>{activity.computer}</span>
                          </div>
                          <div className="overview-context-item flex items-center gap-2 text-xs text-muted-foreground">
                            <User className="w-3 h-3" aria-hidden="true" />
                            <span>{activity.user}</span>
                          </div>
                          <div className="overview-context-item overview-context-path flex items-center gap-2 text-xs text-muted-foreground">
                            <Folder className="w-3 h-3" aria-hidden="true" />
                            <span className="font-mono break-all">{activity.path}</span>
                          </div>
                        </div>
                      </div>
                      {!fromdetails && (
                        <div className="flex items-center gap-1 ml-3 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{activity.time}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : fromdetails ? (
            <OverviewEmptyState icon={Activity} message="No recent activity available in this time range." />
          ) : (
            <div className="py-6 text-center text-muted-foreground">No recent activity available.</div>
          )}
        </div>
        {!loading && totalPages > 1 && (
          <div className="overview-pagination flex flex-wrap items-center justify-center gap-2 mt-auto pt-4 bg-card">
            {fromdetails && <span className="overview-pagination-summary">{startIndex + 1}–{Math.min(startIndex + itemsPerPage, recentdata.length)} of {recentdata.length}</span>}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2"
              aria-label="Previous activity page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {getPagination(currentPage, totalPages).map((page, index) => (
              <Button
                key={`${page}-${index}`}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => page !== "..." && setCurrentPage(Number(page))}
                disabled={page === "..."}
                aria-current={currentPage === page ? "page" : undefined}
                className={`h-8 min-w-8 px-2 ${page === "..." ? "cursor-default opacity-60" : ""}`}
              >
                {page}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2"
              aria-label="Next activity page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
